import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import { getDataAPI, postDataAPI } from "../../../services/apiWrapper";
import { ALERT } from "../../../store/actions/actionTypes";

/**
 * LMS - Quick Test / Practice Test (self-check, nothing stored).
 *
 * 10 random questions for a subject, no timer, no pass mark. On submit the score
 * and the right/wrong answers are shown so the student can learn. Closing or
 * reopening starts fresh - nothing is recorded anywhere.
 *
 * props: subjectId, subjectName, mode ("practice" | "quick"), show, onClose
 */
export default function PracticeTest({ subjectId, subjectName, mode, show, onClose }) {
  const dispatch = useDispatch();
  const token = () => localStorage.getItem("studentToken");
  const alert = (payload) => dispatch({ type: ALERT, payload });

  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({}); // question_id -> mcq_id
  const [result, setResult] = useState(null); // { correct,total,percent,results }

  useEffect(() => {
    if (show && subjectId) load();
    if (!show) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, subjectId, mode]);

  const reset = () => {
    setQuestions([]);
    setAnswers({});
    setResult(null);
  };

  const load = async () => {
    reset();
    setLoading(true);
    try {
      const res = await dispatch(getDataAPI(`student/lms/practice/${subjectId}?mode=${mode}`, token()));
      setQuestions(res.data.data.questions || []);
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not load the test." });
      onClose();
    }
    setLoading(false);
  };

  const pick = (qid, mcqId) => {
    if (result) return; // locked after submit
    setAnswers((a) => ({ ...a, [qid]: mcqId }));
  };

  const submit = async () => {
    try {
      const res = await dispatch(postDataAPI("student/lms/practice/check", { answers }, token()));
      setResult(res.data.data);
    } catch (err) {
      alert({ error: "Could not check your answers." });
    }
  };

  const verdict = (qid) => {
    if (!result) return null;
    return result.results.find((r) => r.question_id === qid);
  };

  const optStyle = (qid, optId) => {
    const base = { display: "block", padding: "8px 12px", marginBottom: 6, borderRadius: 6, border: "1px solid #ccc", cursor: result ? "default" : "pointer" };
    if (!result) {
      return answers[qid] === optId ? { ...base, border: "1px solid #0d6efd", background: "#eaf2ff" } : base;
    }
    const v = verdict(qid);
    if (v && v.correct_mcq_id === optId) return { ...base, border: "1px solid #28a745", background: "#e7f6ec" };
    if (v && v.chosen_mcq_id === optId && !v.is_correct) return { ...base, border: "1px solid #dc3545", background: "#fbeaec" };
    return base;
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === "quick" ? "Quick Test" : "Practice Test"}
          {subjectName ? ` — ${subjectName}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <p>Loading…</p>
        ) : questions.length === 0 ? (
          <p className="text-muted">No questions available.</p>
        ) : (
          <>
            {result && (
              <div className="alert alert-info">
                Score: <b>{result.correct}/{result.total}</b> ({result.percent}%). Correct answers are
                marked in green, your wrong ones in red. This is only a self-check — nothing is saved.
              </div>
            )}
            {questions.map((q, i) => (
              <div key={q.question_id} style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  {i + 1}. {q.question}
                </div>
                {q.options.map((o) => (
                  <label key={o.id} style={optStyle(q.question_id, o.id)}>
                    <input
                      type="radio"
                      name={`pq${q.question_id}`}
                      checked={answers[q.question_id] === o.id}
                      onChange={() => pick(q.question_id, o.id)}
                      disabled={!!result}
                      style={{ marginRight: 8 }}
                    />
                    {o.text}
                  </label>
                ))}
              </div>
            ))}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!result && questions.length > 0 && (
          <Button variant="primary" onClick={submit} disabled={Object.keys(answers).length === 0}>
            Submit
          </Button>
        )}
        {result && (
          <Button variant="outline-primary" onClick={load}>Try again</Button>
        )}
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
